import React, { useEffect, useMemo, useState } from 'react';
import { ArrowForward, BackupOutlined, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputBase,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const ProductCategoryBrowser = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    api.getProductLevel3Categories()
      .then((response) => {
        if (active) setCategories(response?.data?.data || []);
      })
      .catch(() => {
        if (active) setError('Level 3 categories could not be loaded.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, []);

  const filteredCategories = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return categories;
    return categories.filter((category) => [category.name, category.categoryName, category.subcategoryName]
      .some((value) => value?.toLowerCase().includes(term)));
  }, [categories, search]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', pb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Button
          variant="text"
          onClick={() => navigate('/products/allProduct')}
          sx={{ minWidth: 0, p: 0, textTransform: 'none', color: '#1473E6', fontSize: 15 }}
        >
          Products
        </Button>
        <Typography sx={{ color: '#98A2B3' }}>/</Typography>
        <Typography sx={{ color: '#344054', fontSize: 15 }}>Level 3 Categories</Typography>
      </Box>

      <Typography sx={{ color: '#1D1F2C', fontSize: { xs: 28, md: 32 }, fontWeight: 700, lineHeight: 1.25, mb: 0.75 }}>
        Subcategory Level 3
      </Typography>
      <Typography sx={{ color: '#667085', fontSize: 16, mb: 3 }}>
        Choose a Level 3 category to view and manage its products.
      </Typography>

      <Paper
        variant="outlined"
        sx={{ borderColor: '#E0E2E7', borderRadius: 2, overflow: 'hidden', boxShadow: '0 3px 10px rgba(16, 24, 40, 0.04)' }}
      >
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={{ color: '#1D1F2C', fontSize: 16, fontWeight: 700 }}>
              Level 3 categories
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<BackupOutlined />}
              onClick={() => navigate('/backup/create')}
              sx={{ textTransform: 'none', borderColor: '#512DA8', color: '#512DA8', fontWeight: 700 }}
            >
              Backup
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #D0D5DD', borderRadius: 1.5, px: 1.5, width: { xs: '100%', sm: 330 }, backgroundColor: '#fff' }}>
              <Search sx={{ color: '#667085', mr: 1, fontSize: 21 }} />
              <InputBase
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search Level 3 categories"
                sx={{ flex: 1, py: 0.8, fontSize: 14 }}
              />
            </Box>
          </Box>
        </Box>
        <TableContainer sx={{ borderTop: '1px solid #EAECF0' }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                <TableCell sx={{ width: '30%', color: '#475467', fontWeight: 700, fontSize: 14 }}>Subcategory Level 3</TableCell>
                <TableCell sx={{ width: '20%', color: '#475467', fontWeight: 700, fontSize: 14 }}>Category</TableCell>
                <TableCell sx={{ width: '23%', color: '#475467', fontWeight: 700, fontSize: 14 }}>Subcategory</TableCell>
                <TableCell sx={{ width: '13%', color: '#475467', fontWeight: 700, fontSize: 14 }}>Products</TableCell>
                <TableCell align="right" sx={{ width: '14%', color: '#475467', fontWeight: 700, fontSize: 14 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCategories.map((category) => (
                <TableRow key={category._id} hover sx={{ '&:last-child td': { borderBottom: 0 }, '&:hover': { backgroundColor: '#FCFAFF' } }}>
                  <TableCell sx={{ py: 2.1, color: '#1D2939', fontWeight: 700, fontSize: 15 }}>{category.name}</TableCell>
                  <TableCell sx={{ py: 2.1, color: '#475467', fontSize: 14 }}>{category.categoryName || '-'}</TableCell>
                  <TableCell sx={{ py: 2.1, color: '#475467', fontSize: 14 }}>{category.subcategoryName || '-'}</TableCell>
                  <TableCell sx={{ py: 2.1 }}>
                    <Chip
                      label={`${category.productCount || 0} products`}
                      size="small"
                      sx={{
                        minWidth: 92,
                        color: category.productCount ? '#512DA8' : '#667085',
                        backgroundColor: category.productCount ? '#F3EEFF' : '#F2F4F7',
                        fontWeight: 700,
                        '& .MuiChip-label': { px: 1.2 },
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 2.1 }}>
                    <Button
                      variant="contained"
                      disabled={!category.productCategoryId}
                      endIcon={<ArrowForward sx={{ fontSize: 17 }} />}
                      onClick={() => navigate(`/products/category/${category.productCategoryId}?categoryName=${encodeURIComponent(category.name)}`, {
                        state: { categoryName: category.name },
                      })}
                      sx={{
                        minWidth: 138,
                        py: 0.85,
                        textTransform: 'none',
                        backgroundColor: '#512DA8',
                        boxShadow: '0 2px 4px rgba(81, 45, 168, 0.22)',
                        '&:hover': { backgroundColor: '#432584' },
                      }}
                    >
                      View Products
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredCategories.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6, color: '#667085' }}>
                    No Level 3 categories found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ProductCategoryBrowser;
